import { useState, type ChangeEvent } from "react";
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  Popconfirm,
  Radio,
  Select,
  Upload,
} from "antd";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { BlockType, ResourceType } from "../../../../types";
import type { ResourceFileResponse } from "../../../../types/api/getResources";
import type { FormBlockUI } from "../utils";
import {
  BLOCK_TYPE_OPTIONS,
  resolveBlockType,
  resolveResourceType,
  getDownloadFileLabel,
} from "../utils";
import { useLessonForm } from "./LessonFormContext";
import ResourcePreview from "../../../common/ResourcePreview";
import EditorComponent from "../../../EditorComponent/EditorComponent";

const { Option } = Select;

interface BlockItemProps {
  block: FormBlockUI;
  blockIndex: number;
  activityIndex: number;
  totalBlocks: number;
  /** Si está dentro de una sección con manipulativos */
  sectionIndex?: number;
  /** SCRUM-795: si está dentro de una alternativa sin manipulativos */
  alternativeIndex?: number;
  onRemove: () => void;
}

const BlockItem: React.FC<BlockItemProps> = ({
  block,
  blockIndex,
  activityIndex,
  totalBlocks,
  sectionIndex,
  alternativeIndex,
  onRemove,
}) => {
  const {
    resources,
    resourceDetails,
    fileUrls,
    setFileUrls,
    setResourceTypeFilter,
    handleBlockTypeChange,
    handleBlockMaterialsChange,
    handleBlockTextChange,
    handleBlockFileIdChange,
    handleBlockUpdate,
    handleResourceIdsChange,
    handleResourceFileIdChange,
    haveFileIdInBlock,
    handleSectionBlockUpdate,
    handleAlternativeBlockUpdate,
    customUpload,
    setLesson,
  } = useLessonForm();

  /**
   * Un bloque puede vivir suelto en la actividad, dentro de una sección con
   * manipulativos o (SCRUM-795) dentro de una alternativa sin manipulativos.
   * Los dos últimos son el mismo caso desde acá: se editan con un único
   * `update(key, value)` genérico, y sólo `nestedUpdate` sabe a cuál de los dos
   * le está hablando. Los sueltos siguen usando los handlers especializados,
   * que hacen limpiezas extra que sólo aplican a ellos.
   */
  const isNested = sectionIndex !== undefined || alternativeIndex !== undefined;
  const nestedUpdate = (key: keyof FormBlockUI, value: any) => {
    if (alternativeIndex !== undefined) {
      handleAlternativeBlockUpdate(
        activityIndex,
        alternativeIndex,
        blockIndex,
        key,
        value,
      );
      return;
    }
    handleSectionBlockUpdate(
      activityIndex,
      sectionIndex!,
      blockIndex,
      key,
      value,
    );
  };

  const blockConfig = BLOCK_TYPE_OPTIONS[block.type] || {};

  // SCRUM-1020: the resourceId is still nulled locally on a type change
  // (a DOWNLOAD id is invalid under a VIDEO filter) — that part is
  // unchanged. What changes is the surprise: this warns instead of clearing
  // silently. The guard that actually blocks orphaning homeworks runs at
  // save time (ResourceHomeworkImpactModal), not here.
  const [resourceTypeChangeWarning, setResourceTypeChangeWarning] =
    useState(false);

  const updateBlock = (key: keyof FormBlockUI, value: any) => {
    if (isNested) {
      nestedUpdate(key, value);
    } else {
      handleBlockUpdate(activityIndex, blockIndex, key, value);
    }
  };

  const handleTypeChange = (value: BlockType) => {
    if (isNested) {
      nestedUpdate("type", value);
    } else {
      handleBlockTypeChange(activityIndex, blockIndex, value);
    }
  };

  const handleMaterialsChange = (content: string) => {
    if (isNested) {
      nestedUpdate("materials", content);
    } else {
      handleBlockMaterialsChange(activityIndex, blockIndex, content);
    }
  };

  const handleTextChange = (content: string) => {
    if (isNested) {
      nestedUpdate("text", content);
    } else {
      handleBlockTextChange(activityIndex, blockIndex, content);
    }
  };

  const handleFileUpload = async (options: any) => {
    const result = await customUpload(options);
    if (result.fileName?.length) {
      if (isNested) {
        nestedUpdate("file_id", result.fileName);
        nestedUpdate("file_id_url", result.url);
        if (result.url && result.fileName)
          setFileUrls((prev) => ({ ...prev, [result.fileName]: result.url }));
      } else {
        handleBlockFileIdChange(
          activityIndex,
          blockIndex,
          result.fileName,
          result.url,
        );
      }
    }
  };

  const handleResourceTypeChange = (newResourceType: ResourceType) => {
    setResourceTypeFilter(newResourceType);
    if (block.resourceType !== newResourceType) {
      setResourceTypeChangeWarning(!!block.resourceId);
      if (isNested) {
        nestedUpdate("resourceId", null);
      } else {
        handleResourceIdsChange(activityIndex, blockIndex, null);
      }
    }
    if (isNested) {
      nestedUpdate("resourceType", newResourceType);
    } else {
      setLesson((prev) => ({
        ...prev,
        activities: prev.activities.map((activity, i) =>
          i !== activityIndex
            ? activity
            : {
                ...activity,
                blocks: activity.blocks.map((b, bi) =>
                  bi !== blockIndex
                    ? b
                    : { ...b, resourceType: newResourceType },
                ),
              },
        ),
      }));
    }
  };

  const handleResourceSelect = (value: number | null) => {
    setResourceTypeChangeWarning(false);
    if (isNested) {
      nestedUpdate("resourceId", value);
    } else {
      handleResourceIdsChange(activityIndex, blockIndex, value);
    }
  };

  const handleFileSelect = (value: number | null) => {
    if (isNested) {
      nestedUpdate("resourceFileId", value);
    } else {
      handleResourceFileIdChange(activityIndex, blockIndex, value);
    }
  };

  const fileId = isNested
    ? block.file_id
    : haveFileIdInBlock(activityIndex, blockIndex).file_id;
  const hasFile = isNested
    ? !!block.file_id
    : haveFileIdInBlock(activityIndex, blockIndex).haveFile;
  const selectedResourceDetails = block.resourceId
    ? resourceDetails[block.resourceId]
    : null;
  const isDownloadWithMultipleFiles =
    selectedResourceDetails?.type === ResourceType.DOWNLOAD &&
    selectedResourceDetails?.files &&
    selectedResourceDetails.files.length > 1;

  return (
    <div className="block-section">
      <div className="block-header-with-actions">
        <h5>Bloque {blockIndex + 1}</h5>
        <Popconfirm
          title="¿Estás seguro de eliminar este bloque?"
          onConfirm={onRemove}
          okText="Sí"
          cancelText="No"
          disabled={totalBlocks <= 1}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            className="delete-block-button"
            disabled={totalBlocks <= 1}
            title={
              totalBlocks <= 1 ? "No se puede eliminar el único bloque" : ""
            }
          />
        </Popconfirm>
      </div>

      <div className="block-header">
        <Form.Item label="Tipo de bloque" className="block-type">
          <Select
            value={block.type}
            placeholder="Selecciona un tipo de bloque"
            onChange={(value) => value && handleTypeChange(value)}
          >
            {Object.values(BlockType).map((type) => (
              <Option key={type} value={type}>
                {resolveBlockType(type)}
              </Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      {/* Tip de metodología activa */}
      <Form.Item
        label="Tip de metodología activa"
        className="block-methodology-tip"
      >
        <Input.TextArea
          placeholder="Escribe un tip sobre metodología activa para este bloque..."
          value={block.active_methodology_tip || ""}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            updateBlock("active_methodology_tip", e.target.value || null)
          }
          rows={3}
        />
      </Form.Item>

      <Form.Item
        label="Materiales"
        className="block-materials"
        validateStatus={
          block.type === BlockType.IMAGE
            ? undefined
            : block.materials?.trim()
              ? "success"
              : "error"
        }
        help={
          block.type === BlockType.IMAGE
            ? null
            : block.materials?.trim()
              ? null
              : "Los materiales son obligatorios"
        }
      >
        <EditorComponent
          text={block.materials || ""}
          onContentChange={handleMaterialsChange}
        />
      </Form.Item>

      {blockConfig.hasText && (
        <Form.Item
          label="Instrucciones"
          className="block-text"
          validateStatus={block.text?.trim() ? "success" : "error"}
          help={block.text?.trim() ? null : "El texto es obligatorio"}
        >
          <EditorComponent
            text={block.text}
            onContentChange={handleTextChange}
          />
        </Form.Item>
      )}

      {blockConfig.hasImage && (
        <Form.Item
          label="Imagen"
          className="block-image"
          validateStatus={block.file_id ? "success" : "error"}
          help={block.file_id ? null : "La imagen es obligatoria"}
        >
          <Upload listType="picture" customRequest={handleFileUpload}>
            <Button icon={<UploadOutlined />}>
              {hasFile ? "Reemplazar archivo" : "Subir archivo"}
            </Button>
          </Upload>
          {hasFile && <p className="uploaded-filename">📄 {fileId}</p>}
          {block.file_id && (block.file_id_url || fileUrls[block.file_id]) && (
            <>
              <Divider />
              <ResourcePreview
                fileId={block.file_id}
                fileUrl={block.file_id_url || fileUrls[block.file_id]}
              />
            </>
          )}
        </Form.Item>
      )}

      {blockConfig.hasResource && (
        <div className="resource-section">
          <Form.Item
            label="Recurso visual"
            className="resource-type"
            help="Si ya hay un recurso seleccionado, cambiar el tipo de recurso eliminará la selección actual."
          >
            <Radio.Group
              value={block.resourceType || ResourceType.VIDEO}
              onChange={(e) => handleResourceTypeChange(e.target.value)}
            >
              {Object.values(ResourceType).map((type) => (
                <Radio key={type} value={type}>
                  {resolveResourceType(type)}
                </Radio>
              ))}
            </Radio.Group>
          </Form.Item>

          {resourceTypeChangeWarning && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="El recurso anterior se quitó de este bloque"
              description="Este cambio se valida al guardar la lección: si el recurso original tenía tareas asignadas, se te va a pedir resolverlo antes de guardar."
            />
          )}

          <Form.Item
            label="ID de recurso"
            className="resource-ids"
            validateStatus={block.resourceId ? "success" : "error"}
            help={
              block.resourceId ? null : "Seleccionar un recurso es obligatorio"
            }
          >
            <Select
              placeholder={`Selecciona ${resolveResourceType(block.resourceType || ResourceType.VIDEO).toLowerCase()}`}
              value={block.resourceId}
              onChange={(value) => handleResourceSelect(value)}
            >
              {resources
                .filter(
                  (r) => r.type === (block.resourceType || ResourceType.VIDEO),
                )
                .map((r) => (
                  <Option key={r.id} value={r.id}>
                    {`Recurso ${r.id} - ${resolveResourceType(r.type)} - ${r.title}`}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {block.resourceId && isDownloadWithMultipleFiles && (
            <Form.Item
              label="Selecciona PDF del recurso asociado a esta actividad"
              validateStatus={block.resourceFileId ? "success" : "error"}
              help={
                block.resourceFileId
                  ? null
                  : "Debes seleccionar qué PDF de este recurso debe mostrar el modal (incluyendo Picture Cards)."
              }
            >
              <Select
                placeholder="Selecciona el archivo específico (PDFs o Picture Cards)"
                value={block.resourceFileId}
                onChange={(value) => handleFileSelect(value)}
              >
                {selectedResourceDetails!.files!.map(
                  (file: ResourceFileResponse) => (
                    <Option key={file.id} value={file.id}>
                      {getDownloadFileLabel(file)}
                    </Option>
                  ),
                )}
              </Select>
            </Form.Item>
          )}

          {(block.resourceType === ResourceType.SLIDES ||
            block.resourceType === ResourceType.BOOK) && (
            <Form.Item label="Número de página" className="page-number">
              <Input
                type="number"
                value={block.page_number ?? ""}
                placeholder="Número de página"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  updateBlock(
                    "page_number",
                    e.target.value ? Number(e.target.value) : null,
                  )
                }
              />
            </Form.Item>
          )}
        </div>
      )}
    </div>
  );
};

export default BlockItem;
